import { useState } from 'react';

/**
 * getTranslation provides the business logic for parsing translated or original text
 *
 * @param {string} text
 * @param {string} textSource
 * @param {boolean} hasOriginalText
 * @param {boolean} isOwner
 * @param {boolean} showTranslation
 * @returns string
 */
const getTranslation = (text, textSource, hasOriginalText, isOwner, showTranslation) => {
  // The business logic in the API dictates that, at a minimum, the field should
  // always exist and the field_source would be additional data.
  if (!text) {
    return '';
  }

  // If there's no original text then always show the field.
  if (!hasOriginalText) {
    return text;
  }

  // Map owners only ever see their original text.
  if (isOwner) {
    return textSource;
  }

  return showTranslation ? text : textSource;
};

/**
 * useEntityTranslation handles API logic to return appropriate translation information
 *
 * An entity could be a map, waypoint, etc., that follows our convention having
 * a field like { text } and also { text, text_source } for translations.
 *
 * @param {object} entity
 * @param {string} field
 * @param {boolean} isOwner
 * @returns { translation: string, showTranslation: boolean, setShowTranslation: (boolean) => void, isTranslatable: boolean }
 */

const useEntityTranslation = (entity, field, isOwner) => {
  const [showTranslation, setShowTranslation] = useState(false);

  const text = entity[field];
  const textSource = entity[`${field}_source`];
  const hasOriginalText = !!textSource;

  return {
    translation: getTranslation(text, textSource, hasOriginalText, isOwner, showTranslation),
    showTranslation,
    setShowTranslation,
    isTranslatable: !isOwner && hasOriginalText
  };
};

export default useEntityTranslation;
